import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, AuthContextType, UserRole, ExecutiveBriefingConfig } from '../types/index';
import { parseEmployeeDesignations } from './employeeDesignations';
import { parseOrgLevel } from './orgChart';
import { parseSupplierPortalRole, parseSupplierWorkFlags } from './supplierWorkFlags';
import { auth, db, storage } from './firebase';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ensureCrmStorageAccessBeforeUpload, prefetchCrmStorageAccessForRole, resetCrmStorageAccessSyncCache } from './crmStorageAccess';
import { isReservedAuthEmail, resolveLoginIdentifier } from './workstationLogin';
import { isWhitelistedAuthRole } from './userRoles';
import { attachPendingArchitectInvite } from './attachPendingArchitectInvite';

function parseExecutiveBriefingConfig(raw: unknown): ExecutiveBriefingConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const profile = o.profile === 'ceo' || o.profile === 'coo' ? o.profile : undefined;
  if (!profile) return undefined;
  const viewers = Array.isArray(o.viewers)
    ? o.viewers.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    : undefined;
  return {
    enabled: o.enabled === true,
    profile,
    ...(viewers?.length ? { viewers } : {}),
  };
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Session role comes from Firestore `users/{uid}.role` (or supplier claim). Email domain is not staff. */

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const updateProfilePhoto = useCallback(async (file: File) => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) throw new Error('Not signed in');
    const max = 8 * 1024 * 1024;
    if (file.size > max) throw new Error('Image must be 8 MB or smaller');
    if (!file.type.startsWith('image/')) throw new Error('Please choose an image file');
    const ext = (file.name.split('.').pop() || 'jpg').replace(/[^a-z0-9]/gi, '') || 'jpg';
    const path = `users/${u.uid}/profile_${Date.now()}.${ext}`;
    const storageRef = ref(storage, path);
    await ensureCrmStorageAccessBeforeUpload();
    await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
    const url = await getDownloadURL(storageRef);
    await updateProfile(u, { photoURL: url });
    await setDoc(
      doc(db, 'users', u.uid),
      { photoURL: url, displayName: u.displayName || undefined },
      { merge: true }
    );
    setUser(prev => (prev ? { ...prev, photoURL: url } : null));
  }, []);

  const clearProfilePhoto = useCallback(async () => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) throw new Error('Not signed in');
    await updateProfile(u, { photoURL: '' });
    await setDoc(doc(db, 'users', u.uid), { photoURL: null }, { merge: true });
    setUser(prev => (prev ? { ...prev, photoURL: null } : null));
  }, []);

  useEffect(() => {
    let profileUnsubscribe: (() => void) | undefined;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      profileUnsubscribe?.();
      profileUnsubscribe = undefined;

      try {
        if (firebaseUser) {
          if (firebaseUser.isAnonymous) {
            const anonUser: User = {
              id: firebaseUser.uid,
              email: '',
              name: 'Visitor',
              role: 'client',
            };
            setUser(anonUser);
            setLoading(false);
            return;
          }

          let userRole: UserRole = 'client';

          await firebaseUser.getIdToken(true);
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const claimOrgId =
            typeof idTokenResult.claims.rfqSupplierOrgId === 'string'
              ? idTokenResult.claims.rfqSupplierOrgId
              : undefined;

          try {
            const userDocRef = doc(db, 'users', firebaseUser.uid);
            const userDocSnap = await getDoc(userDocRef);
            let fsData: Record<string, unknown> | null = null;
            if (userDocSnap.exists()) {
              fsData = userDocSnap.data() as Record<string, unknown>;
              const rawRole = fsData.role;
              const storedRole = typeof rawRole === 'string' ? rawRole.trim() : '';
              if (isWhitelistedAuthRole(storedRole)) {
                userRole = storedRole.toLowerCase() as UserRole;
              }
            }

            if (userRole === 'client') {
              const attached = await attachPendingArchitectInvite();
              if (attached) {
                const refreshed = await getDoc(userDocRef);
                if (refreshed.exists()) {
                  fsData = refreshed.data() as Record<string, unknown>;
                  const storedRole = typeof fsData.role === 'string' ? fsData.role.trim() : '';
                  if (isWhitelistedAuthRole(storedRole)) {
                    userRole = storedRole.toLowerCase() as UserRole;
                  }
                }
              }
            }

            if (claimOrgId && userRole === 'client') {
              userRole = 'supplier';
            }

            let supplierOrgIdFromFs =
              typeof fsData?.supplierOrgId === 'string' ? fsData.supplierOrgId.trim() : undefined;
            if (supplierOrgIdFromFs === '') supplierOrgIdFromFs = undefined;
            if (!supplierOrgIdFromFs && claimOrgId) {
              supplierOrgIdFromFs = claimOrgId;
            }

            // Invite merges supplierOrgId; older sessions may still have role "client" from prior CRM testing.
            if (supplierOrgIdFromFs && userRole === 'client') {
              userRole = 'supplier';
            }

            const photoFromFs = fsData?.photoURL as string | undefined | null;
            const photoURL =
              (photoFromFs != null && photoFromFs !== '' ? photoFromFs : undefined) ??
              firebaseUser.photoURL ??
              undefined;

            const nameFromFs = fsData?.displayName as string | undefined;
            const displayName =
              nameFromFs ||
              firebaseUser.displayName ||
              firebaseUser.email?.split('@')[0] ||
              'User';

            const managesRaw = fsData?.calendarManagesExecutives;
            const calendarManagesExecutives = Array.isArray(managesRaw)
              ? managesRaw.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
              : undefined;

            const designations = fsData ? parseEmployeeDesignations(fsData.designations) : undefined;
            const supplierDesignations = fsData ? parseSupplierWorkFlags(fsData.supplierDesignations) : [];
            const supplierPortalRole = parseSupplierPortalRole(fsData?.supplierPortalRole);
            const executiveBriefing = fsData ? parseExecutiveBriefingConfig(fsData.executiveBriefing) : undefined;
            const orgLevel = parseOrgLevel(fsData?.orgLevel);

            const seenReleasesRaw = fsData?.seenReleaseIds;
            const seenReleaseIds = Array.isArray(seenReleasesRaw)
              ? seenReleasesRaw.filter((x): x is string => typeof x === 'string' && x.length > 0)
              : undefined;

            const seenCelebrationsRaw = fsData?.seenCelebrationIds;
            const seenCelebrationIds = Array.isArray(seenCelebrationsRaw)
              ? seenCelebrationsRaw.filter((x): x is string => typeof x === 'string' && x.length > 0)
              : undefined;

            const seenAwardGrantsRaw = fsData?.seenAwardGrantIds;
            const seenAwardGrantIds = Array.isArray(seenAwardGrantsRaw)
              ? seenAwardGrantsRaw.filter((x): x is string => typeof x === 'string' && x.length > 0)
              : undefined;

            const seenBugSquashRaw = fsData?.seenBugSquashIds;
            const seenBugSquashIds = Array.isArray(seenBugSquashRaw)
              ? seenBugSquashRaw.filter((x): x is string => typeof x === 'string' && x.length > 0)
              : undefined;

            const is1099Contractor = fsData?.is1099Contractor === true;
            const notifPrefs = fsData?.notificationPrefs as { bellSound?: boolean; bellSoundId?: string } | undefined;
            const bellSound = notifPrefs?.bellSound !== false;
            const bellSoundId = typeof notifPrefs?.bellSoundId === 'string' ? notifPrefs.bellSoundId : undefined;

            const userData: User = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: displayName,
              role: userRole,
              photoURL: photoURL ?? undefined,
              ...(calendarManagesExecutives?.length ? { calendarManagesExecutives } : {}),
              ...(designations?.length ? { designations } : {}),
              ...(supplierDesignations.length ? { supplierDesignations } : {}),
              ...(userRole === 'supplier' ? { supplierPortalRole } : {}),
              ...(executiveBriefing ? { executiveBriefing } : {}),
              ...(orgLevel ? { orgLevel } : {}),
              ...(seenReleaseIds?.length ? { seenReleaseIds } : {}),
              ...(seenCelebrationIds?.length ? { seenCelebrationIds } : {}),
              ...(seenAwardGrantIds?.length ? { seenAwardGrantIds } : {}),
              ...(seenBugSquashIds?.length ? { seenBugSquashIds } : {}),
              ...(is1099Contractor ? { is1099Contractor: true } : {}),
              ...(fsData?.teamChatEnabled === true ? { teamChatEnabled: true } : {}),
              bellSound,
              ...(bellSoundId ? { bellSoundId } : {}),
              ...(userRole === 'supplier' && supplierOrgIdFromFs
                ? { supplierOrgId: supplierOrgIdFromFs }
                : {}),
            };
            setUser(userData);
            prefetchCrmStorageAccessForRole(userRole);

            profileUnsubscribe = onSnapshot(userDocRef, (snap) => {
              if (!snap.exists()) return;
              const live = snap.data() as Record<string, unknown>;
              const liveRoleRaw = typeof live.role === 'string' ? live.role.trim() : '';
              const liveRole = (
                isWhitelistedAuthRole(liveRoleRaw) ? liveRoleRaw.toLowerCase() : null
              ) as UserRole | null;
              const liveIs1099 = live.is1099Contractor === true;
              const liveDesignations = parseEmployeeDesignations(live.designations);
              const liveSupplierDesignations = parseSupplierWorkFlags(live.supplierDesignations);
              const liveSupplierPortalRole = parseSupplierPortalRole(live.supplierPortalRole);
              const liveBriefing = parseExecutiveBriefingConfig(live.executiveBriefing);
              const liveOrgLevel = parseOrgLevel(live.orgLevel);
              const liveSeenAwardRaw = live.seenAwardGrantIds;
              const liveSeenAwardGrantIds = Array.isArray(liveSeenAwardRaw)
                ? liveSeenAwardRaw.filter((x): x is string => typeof x === 'string' && x.length > 0)
                : undefined;
              const livePrefs = live.notificationPrefs as { bellSound?: boolean; bellSoundId?: string } | undefined;
              const liveBellSound = livePrefs?.bellSound !== false;
              const liveBellSoundId = typeof livePrefs?.bellSoundId === 'string' ? livePrefs.bellSoundId : undefined;
              setUser((prev) => {
                if (!prev || prev.id !== firebaseUser.uid) return prev;
                const nextIs1099 = liveIs1099 ? true : undefined;
                const nextDesignations = liveDesignations?.length ? liveDesignations : undefined;
                const nextSupplierDesignations = liveSupplierDesignations.length
                  ? liveSupplierDesignations
                  : undefined;
                const nextSupplierPortalRole =
                  prev.role === 'supplier' ? liveSupplierPortalRole : undefined;
                const nextSeenAwardGrantIds = liveSeenAwardGrantIds?.length
                  ? liveSeenAwardGrantIds
                  : undefined;
                const nextRole = liveRole || prev.role;
                if (
                  prev.role === nextRole
                  && prev.is1099Contractor === nextIs1099
                  && JSON.stringify(prev.designations ?? []) === JSON.stringify(nextDesignations ?? [])
                  && JSON.stringify(prev.supplierDesignations ?? []) === JSON.stringify(nextSupplierDesignations ?? [])
                  && prev.supplierPortalRole === nextSupplierPortalRole
                  && JSON.stringify(prev.executiveBriefing ?? null) === JSON.stringify(liveBriefing ?? null)
                  && (prev.orgLevel || '') === (liveOrgLevel || '')
                  && JSON.stringify(prev.seenAwardGrantIds ?? []) === JSON.stringify(nextSeenAwardGrantIds ?? [])
                  && (prev.bellSound !== false) === liveBellSound
                  && (prev.bellSoundId || '') === (liveBellSoundId || '')
                ) {
                  return prev;
                }
                return {
                  ...prev,
                  role: nextRole,
                  is1099Contractor: nextIs1099,
                  designations: nextDesignations,
                  supplierDesignations: nextSupplierDesignations,
                  supplierPortalRole: nextSupplierPortalRole,
                  executiveBriefing: liveBriefing,
                  orgLevel: liveOrgLevel,
                  seenAwardGrantIds: nextSeenAwardGrantIds,
                  bellSound: liveBellSound,
                  bellSoundId: liveBellSoundId,
                };
              });
            });

            try {
              if (!userDocSnap.exists()) {
                await setDoc(userDocRef, {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName,
                  role: 'client',
                  authProvider: firebaseUser.providerData[0]?.providerId || 'email',
                  photoURL: photoURL ?? null,
                  createdAt: new Date(),
                  lastLogin: new Date(),
                  isActive: true,
                });
              } else {
                const existing = userDocSnap.data() as Record<string, unknown>;
                const updates: Record<string, unknown> = {
                  lastLogin: new Date(),
                  displayName: firebaseUser.displayName || existing.displayName || displayName,
                };
                if (!existing.photoURL && firebaseUser.photoURL) {
                  updates.photoURL = firebaseUser.photoURL;
                }
                await setDoc(userDocRef, updates, { merge: true });
              }
            } catch (firestoreError) {
              console.error('Error saving user to Firestore:', firestoreError);
            }
          } catch (profileErr) {
            console.error('Error resolving user profile from Firestore:', profileErr);
            const emergencyRole: UserRole = claimOrgId ? 'supplier' : 'client';
            const userData: User = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
              role: emergencyRole,
              photoURL: firebaseUser.photoURL ?? undefined,
              ...(emergencyRole === 'supplier' && claimOrgId ? { supplierOrgId: claimOrgId } : {}),
            };
            setUser(userData);
          }
        } else {
          setUser(null);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error in auth state change:', error);
        setLoading(false);
      }
    });

    return () => {
      profileUnsubscribe?.();
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const identifier = resolveLoginIdentifier(email);
      await signInWithEmailAndPassword(auth, identifier, password);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<boolean> => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      await signInWithPopup(auth, provider);
      return true;
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      console.error('Google sign-in failed:', err?.code ?? '', err?.message ?? error);
      throw error;
    }
  };

  const requestPasswordReset = async (email: string): Promise<void> => {
    const identifier = resolveLoginIdentifier(email);
    if (isReservedAuthEmail(identifier)) {
      throw new Error(
        'Password reset is not available for this account. Contact an administrator for help.',
      );
    }
    await sendPasswordResetEmail(auth, identifier, {
      url: 'https://clients.lctimberframes.com/',
      handleCodeInApp: false,
    });
  };

  const logout = async () => {
    resetCrmStorageAccessSyncCache();
    await signOut(auth);
  };

  const value: AuthContextType = {
    user,
    login,
    signInWithGoogle,
    requestPasswordReset,
    logout,
    isAuthenticated: !!user && user.email !== '',
    loading,
    updateProfilePhoto,
    clearProfilePhoto,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
